import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useFlags, useKaliningradRules } from '../api/hooks';
import { ErrorView, Loader } from '../components/StateViews';
import { kgdNum } from '../components/qualification/Kaliningrad';

function BackChevron() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

/** Экран 4 — «Как это работает» (8d): правила за 20 секунд. */
export function KaliningradRulesScreen() {
  const navigate = useNavigate();
  const flags = useFlags();
  const q = useKaliningradRules(Boolean(flags.data?.kaliningrad));
  const [fieldIdx, setFieldIdx] = useState(1); // по умолчанию «25 игроков»
  if (flags.data && !flags.data.kaliningrad) return <Navigate to="/qualification" replace />;
  if (!flags.data || q.isLoading) return <Loader />;
  if (q.isError) return <ErrorView message={(q.error as Error)?.message} />;
  const data = q.data;
  if (!data) return <Navigate to="/qualification" replace />;
  const table = data.tables[Math.min(fieldIdx, data.tables.length - 1)];
  const endsLabel = data.endsAt
    ? new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(new Date(data.endsAt))
    : '';

  // Схлопываем ровный хвост таблицы в одну строку «10-е … N-е».
  const rows = table.rows;
  let headRows = rows;
  let tail: { from: number; to: number; points: number } | null = null;
  if (rows.length > 9) {
    const tailRows = rows.slice(9);
    if (tailRows.every((r) => r.points === tailRows[0].points)) {
      headRows = rows.slice(0, 9);
      tail = { from: tailRows[0].place, to: tailRows[tailRows.length - 1].place, points: tailRows[0].points };
    }
  }

  return (
    <div className="kqm-screen kqr-screen">
      <div className="kqm-nav">
        <button className="kqm-back" onClick={() => navigate(-1)} aria-label="Назад">
          <BackChevron />
        </button>
        <span className="ttl">Как это работает</span>
      </div>

      <div className="kqr-essence">
        {data.gamesTotal} игр, в зачёт идут {data.bestCount} лучших. Четверо лучших{endsLabel ? ` ${endsLabel}` : ''} едут в {data.city}.
      </div>

      <div className="kqr-label mono">Откуда очки</div>
      <div className="kq-card kqr-pool">
        <div className="kqr-cols">
          <div className="col">
            <span className="n mono">{kgdNum(data.pointsConfig.entry)}</span>
            <span className="d">вход</span>
          </div>
          <div className="col">
            <span className="n mono">{kgdNum(data.pointsConfig.reentry)}</span>
            <span className="d">ре-энтри</span>
          </div>
          {data.pointsConfig.guarantee > 0 && (
            <div className="col">
              <span className="n mono gold">{kgdNum(data.pointsConfig.guarantee)}</span>
              <span className="d">не меньше</span>
            </div>
          )}
        </div>
        <div className="kqr-poolnote">
          Чем больше народу и ре-энтри, тем больше пул — и тем дороже каждое место.
        </div>
      </div>

      <div className="kqr-label mono">Очки за места</div>
      <div className="kqr-seg">
        {data.tables.map((t, i) => (
          <button key={t.field} className={i === fieldIdx ? 'active mono' : 'mono'} onClick={() => setFieldIdx(i)}>
            {t.field} игроков
          </button>
        ))}
      </div>
      <div className="kqr-table">
        {headRows.map((r) => (
          <div key={r.place} className="row">
            <span className="pl">{r.place}-е место</span>
            <span className="pt mono">{kgdNum(r.points)}</span>
          </div>
        ))}
        {tail && (
          <div className="row">
            <span className="pl">{tail.from}-е — {tail.to}-е</span>
            <span className="pt mono">{kgdNum(tail.points)}</span>
          </div>
        )}
        <div className="row">
          <span className="pl">ниже {table.paidPlaces}-го</span>
          <span className="pt mono muted">0</span>
        </div>
      </div>
      <div className="kqr-tablenote">Очки получает верхняя половина поля</div>

      <div className="kqr-label mono">Зачёт</div>
      <div className="kqr-scheme">
        {Array.from({ length: data.bestCount }, (_, i) => (
          <i key={i} className={i < data.bestCount - 2 ? 'on' : ''} />
        ))}
      </div>
      <div className="kqr-schemenote">
        Сыграл меньше {data.bestCount} — считаются все. Сыграл больше — худшие выпадают. Плохая игра никогда не уменьшает сумму.
      </div>

      <div className="kqr-fine">
        Ре-энтри идёт в пул, а не лично тебе. Два вылета в одной раздаче делят очки двух мест.
        Тай-брейк — 9-й результат, затем лучший единичный. Отказ от поездки — место переходит следующему.
      </div>
    </div>
  );
}
